import { useState } from "react";
import { DirectDebitNavbar, PoweredBy, hostedPageStyle } from "./DirectDebitChrome";
import TermsModal from "./TermsModal";

const mainStyle = {
  width: "100%",
  margin: "0 auto",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 16,
  boxSizing: "border-box",
};

const cardStyle = {
  width: "100%",
  background: "#FFFFFF",
  borderRadius: 16,
  padding: 24,
  display: "flex",
  flexDirection: "column",
  gap: 16,
  boxSizing: "border-box",
};

const titleStackStyle = {
  display: "flex",
  flexDirection: "column",
  gap: 2,
  width: "100%",
};

const titleStyle = {
  margin: 0,
  fontSize: 18,
  fontWeight: 600,
  lineHeight: "28px",
  color: "#1E2635",
};

const subtitleStyle = {
  margin: 0,
  fontSize: 16,
  fontWeight: 400,
  lineHeight: "24px",
  color: "#505662",
};

const detailStyle = {
  width: "100%",
  border: "1px solid #E1E3E4",
  borderRadius: 16,
  padding: 15,
  display: "flex",
  flexDirection: "column",
  gap: 16,
  boxSizing: "border-box",
};

const logoSlotStyle = {
  position: "relative",
  width: 88,
  height: 32,
  flexShrink: 0,
};

const fieldsStyle = {
  display: "flex",
  flexDirection: "column",
  gap: 16,
  width: "100%",
};

const fieldStyle = {
  display: "flex",
  flexDirection: "column",
  gap: 8,
  width: "100%",
};

const labelRowStyle = {
  display: "flex",
  alignItems: "center",
  gap: 2,
  height: 20,
};

const labelStyle = {
  fontSize: 14,
  fontWeight: 500,
  lineHeight: "20px",
  color: "#1E2635",
};

const requiredStyle = {
  fontSize: 14,
  fontWeight: 500,
  lineHeight: "20px",
  color: "#FD5C63",
};

const inputStyle = {
  width: "100%",
  height: 42,
  boxSizing: "border-box",
  border: "1px solid #E1E3E4",
  borderRadius: 12,
  background: "#FFFFFF",
  padding: "0 15px",
  fontFamily: "inherit",
  fontSize: 14,
  fontWeight: 400,
  lineHeight: "18px",
  color: "#1E2635",
  outline: "none",
};

const actionsStyle = {
  display: "flex",
  flexDirection: "column",
  gap: 8,
  width: "100%",
};

const agreeRowStyle = {
  position: "relative",
  display: "flex",
  alignItems: "center",
  gap: 8,
  width: "100%",
  margin: 0,
  padding: "8px 0",
  boxSizing: "border-box",
};

const checkboxHitStyle = {
  position: "relative",
  width: 24,
  height: 24,
  flexShrink: 0,
  margin: 0,
  cursor: "pointer",
};

const checkboxInputStyle = {
  position: "absolute",
  inset: 0,
  opacity: 0,
  margin: 0,
  cursor: "pointer",
};

const agreeTextStyle = {
  flex: 1,
  minWidth: 0,
  fontSize: 14,
  fontWeight: 400,
  lineHeight: "20px",
  color: "#505662",
};

const termsStyle = {
  display: "inline",
  padding: 0,
  border: "none",
  background: "none",
  font: "inherit",
  fontSize: "inherit",
  lineHeight: "inherit",
  color: "#01793B",
  textDecoration: "underline",
  textDecorationStyle: "solid",
  textUnderlineOffset: "auto",
  cursor: "pointer",
};

const errorStyle = {
  margin: 0,
  fontSize: 12,
  lineHeight: "18px",
  color: "#B42318",
};

const secureStyle = {
  margin: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 4,
  fontSize: 12,
  fontWeight: 400,
  lineHeight: "18px",
  color: "#505662",
};

function formatCard(digits) {
  return digits.replace(/(\d{4})(?=\d)/g, "$1 ");
}

function formatExpiry(digits) {
  if (digits.length < 3) {
    return digits;
  }
  return `${digits.slice(0, 2)} / ${digits.slice(2)}`;
}

function placeCaret(input, formatted, digitIndex) {
  if (digitIndex <= 0) {
    input.setSelectionRange(0, 0);
    return;
  }

  let seen = 0;
  let pos = formatted.length;
  for (let i = 0; i < formatted.length; i += 1) {
    if (/\d/.test(formatted[i])) {
      seen += 1;
      if (seen === digitIndex) {
        pos = i + 1;
        break;
      }
    }
  }
  input.setSelectionRange(pos, pos);
}

function Field({ id, label, value, placeholder, autoComplete, onChange }) {
  return (
    <div style={fieldStyle}>
      <span style={labelRowStyle}>
        <label htmlFor={id} style={labelStyle}>{label}</label>
        <span style={requiredStyle} aria-hidden="true">*</span>
      </span>
      <input
        id={id}
        className="dd-card-field"
        type="text"
        inputMode="numeric"
        autoComplete={autoComplete}
        spellCheck={false}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        style={inputStyle}
      />
    </div>
  );
}

export default function CardEntry({ submitting, error, onSubmit, brandName, logoUrl }) {
  const [cardDigits, setCardDigits] = useState("");
  const [expiryDigits, setExpiryDigits] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);
  const [localError, setLocalError] = useState(null);

  const shownError = localError || error;
  const termsLocked = !agreed;

  const handleDigits = (event, max, format, setDigits) => {
    const input = event.target;
    const raw = input.value;
    const caret = input.selectionStart ?? raw.length;
    const digitsBefore = raw.slice(0, caret).replace(/\D/g, "").length;
    const next = raw.replace(/\D/g, "").slice(0, max);
    setDigits(next);
    setLocalError(null);
    const formatted = format(next);
    const clamped = Math.min(digitsBefore, next.length);
    requestAnimationFrame(() => {
      if (document.activeElement === input) {
        placeCaret(input, formatted, clamped);
      }
    });
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (termsLocked || submitting) {
      return;
    }
    if (cardDigits.length < 13 || cardDigits.length > 19) {
      setLocalError("Enter a valid card number.");
      return;
    }
    const month = Number(expiryDigits.slice(0, 2));
    if (expiryDigits.length !== 4 || month < 1 || month > 12) {
      setLocalError("Enter a valid expiry date.");
      return;
    }
    setLocalError(null);
    onSubmit(cardDigits, `${expiryDigits.slice(0, 2)}/${expiryDigits.slice(2)}`);
  };

  return (
    <div style={hostedPageStyle}>
      <style>{`
        .dd-card-main {
          max-width: 480px;
          padding: 24px 16px 32px;
        }
        .dd-card-form {
          max-width: 480px;
        }
        .dd-card-field::placeholder {
          color: #C0C2C6;
          opacity: 1;
        }
        .dd-card-field:focus {
          border-color: #01793B;
        }
        @media (min-width: 1024px) {
          .dd-card-main {
            flex: 1 0 auto;
            justify-content: center;
            justify-content: safe center;
            max-width: none;
            min-height: calc(100vh - 64px);
            padding: 32px 16px;
          }
          .dd-card-form {
            max-width: 800px;
          }
        }
        .dd-continue {
          width: 100%;
          height: 40px;
          box-sizing: border-box;
          display: flex;
          align-items: center;
          justify-content: center;
          border: none;
          border-radius: 12px;
          padding: 0 16px;
          background: #01793B;
          color: #FFFFFF;
          -webkit-text-fill-color: #FFFFFF;
          opacity: 1;
          font-family: inherit;
          font-size: 14px;
          font-weight: 500;
          line-height: 20px;
          letter-spacing: 0;
          cursor: pointer;
          outline: none;
        }
        .dd-continue:hover:not(:disabled) {
          background: #016531;
        }
        .dd-continue:active:not(:disabled) {
          background: #013D1E;
        }
        .dd-continue:focus:not(:disabled) {
          border: 2px solid rgba(1, 121, 59, 0.2);
          padding: 0 14px;
        }
        .dd-continue:disabled {
          background: #C0C2C6;
          color: #9EA1A8;
          -webkit-text-fill-color: #9EA1A8;
          border: none;
          padding: 0 16px;
          cursor: default;
        }
      `}</style>

      <DirectDebitNavbar brandName={brandName} logoUrl={logoUrl} />

      <main className="dd-card-main" style={mainStyle}>
        <form className="dd-card-form" style={cardStyle} onSubmit={handleSubmit}>
          <div style={titleStackStyle}>
            <h1 style={titleStyle}>Link your Bank Mandiri Card</h1>
            <p style={subtitleStyle}>Enter your card detail to continue</p>
          </div>

          <div style={detailStyle}>
            <span style={logoSlotStyle}>
              <img
                src="/assets/images/direct-debit/mandiri.png"
                alt="Mandiri"
                style={{
                  position: "absolute",
                  left: "50%",
                  top: "50%",
                  width: 88,
                  height: 25.69,
                  transform: "translate(-50%, -50%)",
                  display: "block",
                }}
              />
            </span>

            <div style={fieldsStyle}>
              <Field
                id="dd-card-number"
                label="Card Number"
                value={formatCard(cardDigits)}
                placeholder="1234 5678 9012 3456"
                autoComplete="cc-number"
                onChange={(event) => handleDigits(event, 19, formatCard, setCardDigits)}
              />
              <Field
                id="dd-card-expiry"
                label="Expiry Date"
                value={formatExpiry(expiryDigits)}
                placeholder="MM / YY"
                autoComplete="cc-exp"
                onChange={(event) => handleDigits(event, 4, formatExpiry, setExpiryDigits)}
              />
            </div>
          </div>

          {shownError ? <p style={errorStyle}>{shownError}</p> : null}

          <div style={actionsStyle}>
            <div style={agreeRowStyle}>
              <label style={checkboxHitStyle}>
                <input
                  type="checkbox"
                  checked={agreed}
                  onChange={(event) => setAgreed(event.target.checked)}
                  aria-label="I agree to the Terms & Conditions"
                  style={checkboxInputStyle}
                />
                <img
                  src={agreed
                    ? "/assets/images/direct-debit/check-box.svg"
                    : "/assets/images/direct-debit/check-box-outline-blank.svg"}
                  alt=""
                  width={24}
                  height={24}
                  style={{ width: 24, height: 24, display: "block" }}
                />
              </label>
              <span style={agreeTextStyle}>
                I agree to the{" "}
                <button type="button" onClick={() => setTermsOpen(true)} style={termsStyle}>
                  Terms & Conditions
                </button>
                {" "}authorize access to my bank account
              </span>
            </div>

            <button
              className="dd-continue"
              type="submit"
              disabled={termsLocked || submitting}
              aria-busy={submitting}
            >
              Continue
            </button>
          </div>

          <p style={secureStyle}>
            <img src="/assets/images/direct-debit/lock-outline.svg" alt="" width={14} height={14} style={{ width: 14, height: 14, display: "block" }} />
            Your information is secure and encrypted
          </p>
        </form>

        <PoweredBy />
      </main>
      {termsOpen ? (
        <TermsModal
          agreed={agreed}
          onAgreedChange={setAgreed}
          onClose={() => setTermsOpen(false)}
        />
      ) : null}
    </div>
  );
}
